/**
 * Scope Renderer Registry
 *
 * Maps scope namespace patterns to specialised outcome view components.
 * When no specialised renderer is found for a namespace, the default raw
 * OutcomeTable view is used.
 *
 * To add a new renderer:
 *   1. Create a component in ./renderers/<Name>.tsx
 *   2. Register the pattern + lazy import here.
 */
import React from 'react';
import type { WorkflowRunOutcomeRow } from '../outcomes/WorkflowRunOutcomesPanel';

export interface ScopeRendererProps {
  runId: number;
  runRows: WorkflowRunOutcomeRow[];
  stepRows: WorkflowRunOutcomeRow[];
}

type RendererEntry = {
  /** Regex or exact-match predicate for the scope namespace. */
  match: (namespace: string) => boolean;
  /** Lazy-loaded component. */
  Component: React.LazyExoticComponent<React.ComponentType<ScopeRendererProps>>;
  /** Human label for debug/logging. */
  label: string;
};

/* ── Registry entries ─────────────────────────────────────────── */

const SCOPE_RENDERERS: RendererEntry[] = [
  {
    match: (ns) => ns === 'workflow_run:music_production.create_project',
    Component: React.lazy(() => import('./renderers/MusicProjectOutcomeView')),
    label: 'Music Production — Create Project',
  },
  // Add more entries here as new scope namespaces get specialised UIs.
  // {
  //   match: (ns) => ns.startsWith('workflow_run:data_pipeline.'),
  //   Component: React.lazy(() => import('./renderers/DataPipelineOutcomeView')),
  //   label: 'Data Pipeline',
  // },
];

/* ── Lookup ───────────────────────────────────────────────────── */

/**
 * Returns the specialised renderer component for the given scope namespace,
 * or `null` if no match is found (caller should fall back to raw tables).
 */
export function lookupScopeRenderer(
  namespace: string,
): { Component: React.LazyExoticComponent<React.ComponentType<ScopeRendererProps>>; label: string } | null {
  for (const entry of SCOPE_RENDERERS) {
    if (entry.match(namespace)) {
      return { Component: entry.Component, label: entry.label };
    }
  }
  return null;
}
